/**
 * ClientCodeFormModal — premium section-based Add/Edit Client Code modal.
 * Left section navigator + right configuration area, stable header/footer.
 * All fields, validations, stored values and backend behavior preserved
 * from the legacy single-scroll form. Shared by Add AND Edit.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Truck, Image, X, Upload, Info, FileText, Zap,
  Landmark, GitBranch, Send, ChevronDown, Mail,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { MLC_TAX_HISTORY_PRESETS, CUSTOM_TEXT_MIN, CUSTOM_TEXT_MAX, previewLetterText } from '../../lib/mlcTaxHistory';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const SECTIONS = [
  { id: 'general', label: 'General', icon: Building2, desc: 'Identity & contacts' },
  { id: 'workflow', label: 'Workflow', icon: GitBranch, desc: 'Typing & routing' },
  { id: 'delivery', label: 'Delivery', icon: Send, desc: 'Methods & formats' },
  { id: 'mailaway', label: 'Mail-Away', icon: Truck, desc: 'Shipping defaults' },
  { id: 'billing', label: 'Billing', icon: Landmark, desc: 'Bill To details' },
  { id: 'branding', label: 'Branding', icon: Image, desc: 'Tax & Title PDFs' },
  { id: 'automation', label: 'Automation', icon: Zap, desc: 'Fax automation' },
];

// Maps validation-error field keys -> owning section (for error dots + auto-jump)
const FIELD_SECTION = {
  code: 'general', short_code: 'general', client_name: 'general',
  mailaway_email: 'general', contact_email: 'general',
  tax_delivery_method: 'delivery', title_delivery_method: 'delivery',
  delivery_method: 'delivery', delivery_email: 'delivery', tax_delivery_email: 'delivery', title_delivery_email: 'delivery',
  default_shipping_method: 'mailaway', default_shipping_fee: 'mailaway',
  default_shipping_method_other: 'mailaway',
  mlc_tax_history_custom_text: 'mailaway',
  use_fax_automation: 'automation',
};

const FieldError = ({ msg }) => msg ? <p className="text-red-600 text-xs mt-1">{msg}</p> : null;
const Hint = ({ children }) => <p className="text-xs text-slate-400 mt-1 leading-relaxed">{children}</p>;

const SectionShell = ({ title, blurb, children }) => (
  <div>
    <div className="mb-4">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</h3>
      {blurb && <p className="text-sm text-slate-500 mt-0.5">{blurb}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

/* ─────────────────────────── Sections ─────────────────────────── */

const GeneralSection = ({ formData, formErrors, onChange, isEdit }) => (
  <SectionShell title="General Information" blurb="Client identity, order prefix and primary contacts.">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm">Full Client Code <span className="text-red-500">*</span></Label>
        <Input autoFocus value={formData.code} onChange={(e) => onChange('code', e.target.value)}
          placeholder="e.g., CLIENT-001" className={`h-9 mt-1 ${formErrors.code ? 'border-red-400' : ''}`}
          data-testid="input-code" />
        <FieldError msg={formErrors.code} />
      </div>
      <div>
        <Label className="text-sm">Short Code <span className="text-red-500">*</span></Label>
        <Input value={formData.short_code} onChange={(e) => onChange('short_code', e.target.value.toUpperCase())}
          placeholder="e.g., CL01" maxLength={4}
          className={`h-9 mt-1 ${formErrors.short_code ? 'border-red-400' : ''}`} data-testid="input-short-code" />
        <FieldError msg={formErrors.short_code} />
        <Hint>2–4 chars for order prefix</Hint>
      </div>
    </div>
    <div>
      <Label className="text-sm">Client Name <span className="text-red-500">*</span></Label>
      <Input value={formData.client_name} onChange={(e) => onChange('client_name', e.target.value)}
        placeholder="Company or Client Name" className={`h-9 mt-1 ${formErrors.client_name ? 'border-red-400' : ''}`}
        data-testid="input-client-name" />
      <FieldError msg={formErrors.client_name} />
    </div>
    <div>
      <Label className="text-sm">Description (Optional)</Label>
      <Textarea value={formData.description} onChange={(e) => onChange('description', e.target.value)}
        placeholder="Additional notes about this client" rows={2}
        className="min-h-[60px] resize-none mt-1" data-testid="input-description" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm">Mail-Away Email <span className="text-red-500">*</span></Label>
        <Input type="email" value={formData.mailaway_email} onChange={(e) => onChange('mailaway_email', e.target.value)}
          placeholder="mailaway@client.com" className={`h-9 mt-1 ${formErrors.mailaway_email ? 'border-red-400' : ''}`}
          data-testid="input-mailaway-email" />
        <FieldError msg={formErrors.mailaway_email} />
      </div>
      <div>
        <Label className="text-sm">Contact Email</Label>
        <Input type="email" value={formData.contact_email} onChange={(e) => onChange('contact_email', e.target.value)}
          placeholder="contact@client.com" className={`h-9 mt-1 ${formErrors.contact_email ? 'border-red-400' : ''}`}
          data-testid="input-contact-email" />
        <FieldError msg={formErrors.contact_email} />
      </div>
    </div>
  </SectionShell>
);

const WorkflowSection = ({ formData, onChange }) => (
  <SectionShell title="Workflow Configuration">
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-6">
      <Label className="text-sm font-medium text-slate-800">Typing Required (Title Search)</Label>
      <div className="w-28 shrink-0">
        <Select value={formData.title_typing_required ? 'yes' : 'no'}
          onValueChange={(v) => onChange('title_typing_required', v === 'yes')}>
          <SelectTrigger className="h-9" data-testid="select-title-typing-required">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    {/* BILLING-DNB-001 — client-level Do Not Bill (billing exclusion only; not a $0 price) */}
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="client-dnb-card">
      <div className="flex items-center justify-between gap-6 min-h-9">
        <Label className="text-sm font-medium text-slate-800">Do Not Bill (DNB)</Label>
        <Switch checked={!!formData.do_not_bill} onCheckedChange={(v) => onChange('do_not_bill', v)} data-testid="switch-do-not-bill" aria-label="Do Not Bill" />
      </div>
      {formData.do_not_bill && (
        <div className="mt-3">
          <Label className="text-xs">Reason <span className="text-red-600">*</span></Label>
          <Input value={formData.dnb_reason || ''} onChange={(e) => onChange('dnb_reason', e.target.value)} maxLength={300}
            placeholder="e.g. Internal / test account — not invoiced" className="h-9 mt-1" data-testid="input-dnb-reason" />
        </div>
      )}
      {Array.isArray(formData.dnb_history) && formData.dnb_history.length > 0 && (
        <ul className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-[11px] text-slate-500" data-testid="dnb-history">
          {[...formData.dnb_history].reverse().slice(0, 5).map((h, i) => (
            <li key={i}>DNB {h.action} by {h.by_name || h.by} · {h.at ? new Date(h.at).toLocaleString() : ''}{h.reason ? ` — ${h.reason}` : ''}</li>
          ))}
        </ul>
      )}
    </div>
  </SectionShell>
);

const InfoTip = ({ text, testId }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <button type="button" aria-label={`About ${text.slice(0, 24)}`} data-testid={testId}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 transition-colors">
        <Info className="h-3.5 w-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[260px] bg-slate-900 text-white leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);

const DeliveryCard = ({ label, tip, tipId, error, children, className = '' }) => (
  <div className={`rounded-xl border bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${error ? 'border-red-300' : 'border-slate-200'} ${className}`}>
    <div className="flex items-center gap-1.5 mb-2">
      <Label className="text-sm font-semibold text-slate-800">{label}</Label>
      {tip && <InfoTip text={tip} testId={tipId} />}
    </div>
    {children}
    <FieldError msg={error} />
  </div>
);

const METHOD_TIPS = {
  tax: 'Applies to all Tax workflow products (Tax Search, Update Tax Search, …). MLS, HOA and Utility inherit this unless set below.',
  title: 'Applies to all Title Search workflow products (Current Owner, Two Owner, Full, O&E, Deed, Mortgage, …).',
  mls_delivery_method: 'MLS and MLS Update products. Leave on Inherit to follow the Tax Search method.',
  hoa_delivery_method: 'HOA products. Leave on Inherit to follow the Tax Search method.',
  utility_delivery_method: 'Utility products. Leave on Inherit to follow the Tax Search method.',
  email: 'Completed products are auto-emailed to the recipient for their category: Tax Search (also MLS, HOA and Utility) or Title Search. An empty category field falls back to the legacy Completion Email so existing clients keep working unchanged.',
  fee: 'Adds an inline Fee Summary (Tax Cert Fee + Additional Fees + Total) to the completion email. No invoice PDF is generated; skipped if the order has no pricing snapshot.',
  format: 'Combined merges everything into one PDF. Separate sends the certificate as PDF + editable DOCX with supporting documents attached individually.',
};

const DeliverySection = ({ formData, formErrors, onChange }) => {
  const inherited = formData.tax_delivery_method === 'external_portal' ? 'External Portal' : 'Internal Only';
  const showTaxEmail = formData.tax_delivery_method !== 'external_portal';
  const showTitleEmail = formData.title_delivery_method !== 'external_portal';
  const showEmail = showTaxEmail || showTitleEmail;
  const legacyEmail = (formData.delivery_email || '').trim();
  return (
    <TooltipProvider>
      <SectionShell title="Delivery Configuration" blurb="Set delivery methods and completion email behavior for each product category.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['tax_delivery_method', 'Tax Search Delivery Method', METHOD_TIPS.tax, 'select-tax-delivery-method', 'tip-tax-delivery-method'],
            ['title_delivery_method', 'Title Search Delivery Method', METHOD_TIPS.title, 'select-title-delivery-method', 'tip-title-delivery-method'],
          ].map(([field, label, tip, testId, tipId]) => (
            <DeliveryCard key={field} label={label} tip={tip} tipId={tipId} error={formErrors[field]}>
              <Select value={formData[field]} onValueChange={(v) => onChange(field, v)}>
                <SelectTrigger className={`h-10 ${formErrors[field] ? 'border-red-400' : ''}`} data-testid={testId}>
                  <SelectValue placeholder="Select delivery method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="external_portal">External Portal</SelectItem>
                  <SelectItem value="internal_only">Internal Only</SelectItem>
                </SelectContent>
              </Select>
            </DeliveryCard>
          ))}
        </div>

        {/* DELIVERY-CONFIG (Sep 2026): per-category overrides for the Tax family. Null = inherit. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="delivery-category-overrides">
          {[
            ['mls_delivery_method', 'MLS Delivery Method'],
            ['hoa_delivery_method', 'HOA Delivery Method'],
            ['utility_delivery_method', 'Utility Delivery Method'],
          ].map(([field, label]) => (
            <DeliveryCard key={field} label={label} tip={METHOD_TIPS[field]} tipId={`tip-${field.replace(/_/g, '-')}`} className="!p-3">
              <Select value={formData[field] || 'inherit'} onValueChange={(v) => onChange(field, v === 'inherit' ? null : v)}>
                <SelectTrigger className="h-10 text-[13px]" data-testid={`select-${field.replace(/_/g, '-')}`}>
                  <SelectValue>
                    {formData[field] === 'external_portal' ? 'External Portal' : formData[field] === 'internal_only' ? 'Internal Only' : 'Inherit from Tax Search'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit">Inherit from Tax Search ({inherited})</SelectItem>
                  <SelectItem value="external_portal">External Portal</SelectItem>
                  <SelectItem value="internal_only">Internal Only</SelectItem>
                </SelectContent>
              </Select>
            </DeliveryCard>
          ))}
        </div>

        {showEmail && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="delivery-email-card">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-slate-900">Delivery Email</h4>
              <InfoTip text={METHOD_TIPS.email} testId="tip-delivery-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showTaxEmail && (
                <div data-testid="tax-delivery-email-field">
                  <Label className="text-sm text-slate-700">Tax Search Delivery Email <span className="text-xs text-slate-400 font-normal">(also MLS · HOA · Utility)</span></Label>
                  <Input type="email" value={formData.tax_delivery_email || ''} onChange={(e) => onChange('tax_delivery_email', e.target.value)}
                    placeholder="Enter Tax Search Delivery Email" className={`h-10 mt-1.5 ${formErrors.tax_delivery_email ? 'border-red-400' : ''}`}
                    data-testid="input-tax-delivery-email" />
                  <FieldError msg={formErrors.tax_delivery_email} />
                  {!(formData.tax_delivery_email || '').trim() && (
                    <p className="mt-1.5 text-[11.5px] text-slate-500" data-testid="tax-delivery-email-fallback-note">
                      {legacyEmail ? <>If left blank, Tax Search deliveries will use the Completion Email: <b className="text-slate-700">{legacyEmail}</b>.</> : 'If left blank, Tax Search deliveries will use the Completion Email below (not configured yet).'}
                    </p>
                  )}
                </div>
              )}
              {showTitleEmail && (
                <div data-testid="title-delivery-email-field">
                  <Label className="text-sm text-slate-700">Title Search Delivery Email</Label>
                  <Input type="email" value={formData.title_delivery_email || ''} onChange={(e) => onChange('title_delivery_email', e.target.value)}
                    placeholder="Enter Title Search Delivery Email" className={`h-10 mt-1.5 ${formErrors.title_delivery_email ? 'border-red-400' : ''}`}
                    data-testid="input-title-delivery-email" />
                  <FieldError msg={formErrors.title_delivery_email} />
                  {!(formData.title_delivery_email || '').trim() && (
                    <p className="mt-1.5 text-[11.5px] text-slate-500" data-testid="title-delivery-email-fallback-note">
                      {legacyEmail ? <>If left blank, Title Search deliveries will use the Completion Email: <b className="text-slate-700">{legacyEmail}</b>.</> : 'If left blank, Title Search deliveries will use the Completion Email below (not configured yet).'}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-dashed border-slate-200" data-testid="legacy-delivery-email-field">
              <Label className="text-sm text-slate-700">Completion Email <span className="text-xs text-slate-400 font-normal">(legacy fallback · used when a category email above is empty)</span></Label>
              <Input type="email" value={formData.delivery_email} onChange={(e) => onChange('delivery_email', e.target.value)}
                placeholder="delivery@client.com" className={`h-10 mt-1.5 ${formErrors.delivery_email ? 'border-red-400' : ''}`}
                data-testid="input-delivery-email" />
              <FieldError msg={formErrors.delivery_email} />
              <p className="mt-1.5 text-[11.5px] text-slate-500" data-testid="delivery-email-routing-summary">
                Routing: Tax / MLS / HOA / Utility → <b>{showTaxEmail ? ((formData.tax_delivery_email || '').trim() || legacyEmail || 'not configured') : 'External Portal (no email)'}</b>
                {' · '}Title Search → <b>{showTitleEmail ? ((formData.title_delivery_email || '').trim() || legacyEmail || 'not configured') : 'External Portal (no email)'}</b>
              </p>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 ${showEmail ? 'sm:grid-cols-2' : ''} gap-3`}>
          {showEmail && (
            <DeliveryCard label="Include Fee Summary in Completion Email" tip={METHOD_TIPS.fee} tipId="tip-fee-summary">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500 leading-relaxed">Include an inline fee summary (Tax Cert Fee + Additional Fees + Total).</p>
                <Switch checked={formData.show_fee_summary_in_delivery_email || false}
                  onCheckedChange={(checked) => onChange('show_fee_summary_in_delivery_email', checked)}
                  data-testid="toggle-fee-summary-in-delivery-email" />
              </div>
            </DeliveryCard>
          )}
          <DeliveryCard label="Tax Certificate Delivery Format" tip={METHOD_TIPS.format} tipId="tip-tax-cert-format">
            <Select value={formData.tax_certificate_delivery_format || 'combined_pdf'}
              onValueChange={(v) => onChange('tax_certificate_delivery_format', v)}>
              <SelectTrigger className="h-10" data-testid="select-tax-cert-delivery-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="combined_pdf">Combined PDF Package (default)</SelectItem>
                <SelectItem value="separate_attachments">Separate Attachments (PDF + DOCX + supporting files)</SelectItem>
              </SelectContent>
            </Select>
          </DeliveryCard>
        </div>
      </SectionShell>
    </TooltipProvider>
  );
};

const MailAwaySection = ({ formData, formErrors, onChange }) => (
  <SectionShell title="Mail-Away Shipping Defaults" blurb="Auto-populated in Mail-Away requests for this client.">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm">Default Shipping Method <span className="text-red-500">*</span></Label>
        <Select value={formData.default_shipping_method || 'none'}
          onValueChange={(v) => {
            const actualValue = v === 'none' ? '' : v;
            onChange('default_shipping_method', actualValue);
            if (v !== 'Other') onChange('default_shipping_method_other', '');
          }}>
          <SelectTrigger className={`h-9 mt-1 ${formErrors.default_shipping_method ? 'border-red-400' : ''}`}
            data-testid="select-shipping-method">
            <SelectValue placeholder="Select shipping method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- Select --</SelectItem>
            <SelectItem value="FedEx">FedEx</SelectItem>
            <SelectItem value="FedEx Low Priority">FedEx Low Priority</SelectItem>
            <SelectItem value="UPS">UPS</SelectItem>
            <SelectItem value="USPS">USPS</SelectItem>
            <SelectItem value="Courier Pickup">Courier Pickup</SelectItem>
            <SelectItem value="Client Account">Client Account</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
        <FieldError msg={formErrors.default_shipping_method} />
      </div>
      <div>
        <Label className="text-sm">Default Shipping Fee <span className="text-red-500">*</span></Label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">$</span>
          <Input type="number" step="0.01" min="0" value={formData.default_shipping_fee}
            onChange={(e) => onChange('default_shipping_fee', e.target.value)} placeholder="0.00"
            className={`pl-7 h-9 ${formErrors.default_shipping_fee ? 'border-red-400' : ''}`}
            data-testid="input-shipping-fee" />
        </div>
        <FieldError msg={formErrors.default_shipping_fee} />
      </div>
    </div>
    {formData.default_shipping_method === 'Other' && (
      <div>
        <Label className="text-sm">Specify Shipping Method <span className="text-red-500">*</span></Label>
        <Input value={formData.default_shipping_method_other}
          onChange={(e) => onChange('default_shipping_method_other', e.target.value)}
          placeholder="e.g., FedEx 2Day AM, USPS Certified Mail" className="h-9 mt-1"
          data-testid="input-shipping-method-other" />
        <FieldError msg={formErrors.default_shipping_method_other} />
      </div>
    )}
    <Hint>These defaults will auto-populate in Mail-Away requests for this client.</Hint>

    {/* MLC-TAXHIST-001 — client-specific Tax History requirement for Current-Tax MLC letters */}
    <div className="pt-4 mt-2 border-t border-slate-200">
      <Label className="text-sm">MLC Tax History Requirement</Label>
      <Select value={formData.mlc_tax_history_preset || 'standard'}
        onValueChange={(v) => onChange('mlc_tax_history_preset', v)}>
        <SelectTrigger className="h-9 mt-1" data-testid="select-mlc-tax-history">
          <SelectValue placeholder="Standard" />
        </SelectTrigger>
        <SelectContent>
          {MLC_TAX_HISTORY_PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value} data-testid={`mlc-tax-history-option-${p.value.replace(':', '-')}`}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {formData.mlc_tax_history_preset === 'custom' && (
        <div className="mt-2">
          <Textarea value={formData.mlc_tax_history_custom_text || ''}
            onChange={(e) => onChange('mlc_tax_history_custom_text', e.target.value)}
            rows={3} maxLength={CUSTOM_TEXT_MAX}
            placeholder="Exact sentence to print on the MLC letter in place of the current-year tax paragraph"
            className={`text-sm ${formErrors.mlc_tax_history_custom_text ? 'border-red-400' : ''}`}
            data-testid="input-mlc-tax-history-custom" />
          <div className="text-[11px] text-slate-400 mt-1">
            Plain text, {CUSTOM_TEXT_MIN}–{CUSTOM_TEXT_MAX} characters. {(formData.mlc_tax_history_custom_text || '').trim().length}/{CUSTOM_TEXT_MAX}
          </div>
          {formErrors.mlc_tax_history_custom_text && (
            <p className="text-red-600 text-xs mt-1" data-testid="field-error-mlc-tax-history-custom">{formErrors.mlc_tax_history_custom_text}</p>
          )}
        </div>
      )}
      <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="mlc-tax-history-preview">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Letter will print</div>
        <p className="text-xs text-slate-700 leading-relaxed">
          {previewLetterText(formData.mlc_tax_history_preset, formData.mlc_tax_history_custom_text) || <span className="italic text-slate-400">Enter the custom wording above.</span>}
        </p>
      </div>
      <Hint>
        Standard keeps today's letter unchanged. Any other choice replaces the current-year tax paragraph on
        Current-Tax MLC letters only (delinquent Tax Claim Bureau letters are not affected). The requirement is
        frozen on each Mail-Away request when it is created; changing it later never alters existing requests.
      </Hint>
    </div>
  </SectionShell>
);

const BillingSection = ({ formData, onChange }) => (
  <SectionShell title="Customer Billing / Bill To"
    blurb={<>Printed in the <strong>Bill To</strong> block of invoice PDFs — different from your company billing settings (Admin → Billing Settings), which configure the <strong>From</strong> block.</>}>
    <div data-testid="section-billing" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Billing Company Name</Label>
          <Input value={formData.billing_company_name} onChange={(e) => onChange('billing_company_name', e.target.value)}
            placeholder="ACME Holdings, Inc." className="mt-1" data-testid="input-billing-company-name" />
        </div>
        <div>
          <Label className="text-xs">Billing Contact Name</Label>
          <Input value={formData.billing_contact_name} onChange={(e) => onChange('billing_contact_name', e.target.value)}
            placeholder="Jane Doe" className="mt-1" data-testid="input-billing-contact-name" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Billing Email <span className="text-rose-500">*</span></Label>
          <Input type="email" value={formData.billing_email} onChange={(e) => onChange('billing_email', e.target.value)}
            placeholder="ap@acme.com" className="mt-1" data-testid="input-billing-email" />
        </div>
        <div>
          <Label className="text-xs">Billing Phone</Label>
          <Input value={formData.billing_phone} onChange={(e) => onChange('billing_phone', e.target.value)}
            placeholder="(555) 123-4567" className="mt-1" data-testid="input-billing-phone" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Address Line 1 <span className="text-rose-500">*</span></Label>
          <Input value={formData.billing_address_line1} onChange={(e) => onChange('billing_address_line1', e.target.value)}
            placeholder="123 Main St" className="mt-1" data-testid="input-billing-address-line1" />
        </div>
        <div>
          <Label className="text-xs">Address Line 2</Label>
          <Input value={formData.billing_address_line2} onChange={(e) => onChange('billing_address_line2', e.target.value)}
            placeholder="Suite 400" className="mt-1" data-testid="input-billing-address-line2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">City</Label>
          <Input value={formData.billing_city} onChange={(e) => onChange('billing_city', e.target.value)}
            placeholder="Springfield" className="mt-1" data-testid="input-billing-city" />
        </div>
        <div>
          <Label className="text-xs">State</Label>
          <Input value={formData.billing_state} onChange={(e) => onChange('billing_state', e.target.value)}
            placeholder="IL" maxLength={2} className="mt-1" data-testid="input-billing-state" />
        </div>
        <div>
          <Label className="text-xs">ZIP</Label>
          <Input value={formData.billing_zip} onChange={(e) => onChange('billing_zip', e.target.value)}
            placeholder="62701" className="mt-1" data-testid="input-billing-zip" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Billing CC (optional)</Label>
          <Input type="email" value={formData.billing_ap_email} onChange={(e) => onChange('billing_ap_email', e.target.value)}
            placeholder="accounts.payable@acme.com" className="mt-1" data-testid="input-billing-ap-email" />
          <Hint>CC'd on invoice emails when set.</Hint>
        </div>
        <div>
          <Label className="text-xs">Invoice Delivery Preference</Label>
          <Select value={formData.billing_invoice_delivery} onValueChange={(v) => onChange('billing_invoice_delivery', v)}>
            <SelectTrigger className="mt-1" data-testid="select-billing-invoice-delivery">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email (default)</SelectItem>
              <SelectItem value="portal">Portal upload</SelectItem>
              <SelectItem value="mail">Postal mail</SelectItem>
              <SelectItem value="none">None / manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </SectionShell>
);

const BrandingSection = ({ formData, onChange, logoPreview, existingLogoUrl, setLogoFile, setLogoPreview, setExistingLogoUrl, editingCode, API }) => (
  <SectionShell title="PDF Branding" blurb="Client logo and identity used on the Tax Certificate and the Title Search Report.">
    <div className="flex items-center justify-between gap-6 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
      <div className="min-w-0">
        <Label className="text-sm font-medium">Use Client Branding on Tax Certificate &amp; Title Search Report</Label>
        <p className="text-xs text-slate-500 mt-0.5">When enabled, the configured logo, display name and contact details are printed on both PDFs. When disabled, the Title Search Report header stays blank (no company branding is assumed).</p>
      </div>
      <Switch checked={formData.use_client_branding || false}
        onCheckedChange={(checked) => onChange('use_client_branding', checked)}
        data-testid="toggle-client-branding" />
    </div>

    {formData.use_client_branding && (
      <div className="space-y-4">
        <div>
          <Label className="text-sm">Client Logo</Label>
          <div className="flex items-center gap-3 mt-1">
            {(logoPreview || existingLogoUrl) && (
              <div className="relative h-12 w-24 border border-slate-200 rounded-md overflow-hidden bg-white flex items-center justify-center">
                <img src={logoPreview || `${API}${existingLogoUrl}`} alt="Client logo"
                  className="max-h-10 max-w-20 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <button type="button"
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                  onClick={async () => {
                    if (existingLogoUrl && editingCode) {
                      try {
                        await axios.delete(`${API}/client-codes/${editingCode.id}/logo`);
                        toast.success('Logo removed');
                      } catch (e) { console.error(e); }
                    }
                    setLogoFile(null);
                    setLogoPreview(null);
                    setExistingLogoUrl(null);
                  }}
                  data-testid="remove-logo-btn">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <label className="cursor-pointer">
              <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 text-sm text-slate-700">
                <Upload className="h-3.5 w-3.5" />
                {logoPreview || existingLogoUrl ? 'Replace' : 'Upload Logo'}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) {
                    toast.error('Logo must be under 2MB');
                    return;
                  }
                  setLogoFile(file);
                  setLogoPreview(URL.createObjectURL(file));
                }}
                data-testid="logo-file-input" />
            </label>
          </div>
          <Hint>PNG, JPG, SVG, or WebP. Transparent PNG recommended. Max 2MB.</Hint>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">PDF Display Name</Label>
            <Input value={formData.certificate_display_name}
              onChange={(e) => onChange('certificate_display_name', e.target.value)}
              placeholder="e.g., Acme Title Company" className="h-9 mt-1" data-testid="input-display-name" />
            <Hint>Brand / display name only. Do NOT enter phone or email here — use the dedicated fields.</Hint>
          </div>
          <div>
            <Label className="text-sm">Disclaimer Entity Name</Label>
            <Input value={formData.certificate_disclaimer_entity}
              onChange={(e) => onChange('certificate_disclaimer_entity', e.target.value)}
              placeholder="e.g., Acme Title Services LLC" className="h-9 mt-1" data-testid="input-disclaimer-entity" />
            <Hint>Legal entity name used in the Conditions &amp; Disclaimers section. Separate from display name.</Hint>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Phone (page-1 PDF header)</Label>
            <Input value={formData.certificate_phone} onChange={(e) => onChange('certificate_phone', e.target.value)}
              placeholder="e.g., 855 311 5969" className="h-9 mt-1" data-testid="input-certificate-phone" />
            <Hint>Shown right-aligned in the page-1 header. Leave blank to omit.</Hint>
          </div>
          <div>
            <Label className="text-sm">Email (page-1 PDF header)</Label>
            <Input type="email" value={formData.certificate_email}
              onChange={(e) => onChange('certificate_email', e.target.value)}
              placeholder="e.g., title@example.com" className="h-9 mt-1" data-testid="input-certificate-email" />
            <Hint>Shown right-aligned in the page-1 header. Leave blank to omit.</Hint>
          </div>
        </div>
      </div>
    )}
  </SectionShell>
);

const AutomationSection = ({ formData, formErrors, onChange }) => (
  <SectionShell title="Fax Automation" blurb="Client-level eFax gateway behavior for pending submissions.">
    <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
      <div className="flex items-center justify-between gap-6 p-4">
        <Label className="text-sm font-medium">Enable Fax for this Client</Label>
        <button type="button" role="switch" aria-checked={!!formData.fax_enabled} data-testid="fax-enabled-toggle"
          onClick={() => onChange('fax_enabled', !formData.fax_enabled)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${formData.fax_enabled ? 'bg-blue-600' : 'bg-gray-200'}`}>
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${formData.fax_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
      <div className={`flex items-center justify-between gap-6 p-4 transition-opacity duration-200 ${formData.fax_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <div className="min-w-0">
          <Label className="text-sm font-medium">Auto-send Fax on Pending Submission</Label>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">When enabled, selecting the "Fax sent" chip + Submit as Pending will auto-generate and send a fax request via the global eFax gateway.</p>
        </div>
        <button type="button" role="switch" aria-checked={!!formData.use_fax_automation}
          data-testid="use-fax-automation-toggle" disabled={!formData.fax_enabled}
          onClick={() => onChange('use_fax_automation', !formData.use_fax_automation)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${formData.use_fax_automation ? 'bg-blue-600' : 'bg-gray-200'} ${!formData.fax_enabled ? 'cursor-not-allowed' : ''}`}>
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${formData.use_fax_automation ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
    </div>
    <FieldError msg={formErrors.use_fax_automation} />
  </SectionShell>
);

/* ─────────────────────────── Modal ─────────────────────────── */

export const ClientCodeFormModal = ({
  open, onClose, editingCode, formData, formErrors, onFieldChange, onSubmit, isLoading,
  logoPreview, existingLogoUrl, setLogoFile, setLogoPreview, setExistingLogoUrl, API,
}) => {
  const [activeSection, setActiveSection] = useState('general');

  // Reset to General each time the modal opens
  useEffect(() => {
    if (open) setActiveSection('general');
  }, [open]);

  // Sections that currently hold validation errors (dot indicator + auto-jump)
  const errorSections = new Set(
    Object.keys(formErrors).filter((k) => formErrors[k]).map((k) => FIELD_SECTION[k]).filter(Boolean)
  );
  const prevErrCount = React.useRef(0);
  useEffect(() => {
    const errKeys = Object.keys(formErrors).filter((k) => formErrors[k]);
    const grew = errKeys.length > prevErrCount.current;
    prevErrCount.current = errKeys.length;
    if (errKeys.length === 0 || !grew) return; // jump only on a fresh validation run
    const first = SECTIONS.find((s) => errKeys.some((k) => FIELD_SECTION[k] === s.id));
    if (first) setActiveSection(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  const sectionProps = { formData, formErrors, onChange: onFieldChange };

  const renderSection = () => {
    switch (activeSection) {
      case 'general': return <GeneralSection {...sectionProps} isEdit={!!editingCode} />;
      case 'workflow': return <WorkflowSection {...sectionProps} />;
      case 'delivery': return <DeliverySection {...sectionProps} />;
      case 'mailaway': return <MailAwaySection {...sectionProps} />;
      case 'billing': return <BillingSection {...sectionProps} />;
      case 'branding':
        return <BrandingSection {...sectionProps}
          logoPreview={logoPreview} existingLogoUrl={existingLogoUrl}
          setLogoFile={setLogoFile} setLogoPreview={setLogoPreview}
          setExistingLogoUrl={setExistingLogoUrl} editingCode={editingCode} API={API} />;
      case 'automation': return <AutomationSection {...sectionProps} />;
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="p-0 gap-0 sm:max-w-[960px] w-[calc(100vw-2rem)] h-[85vh] max-h-[720px] flex flex-col overflow-hidden"
        data-testid="client-code-form-modal"
        onKeyDown={(e) => {
          const role = e.target.getAttribute && e.target.getAttribute('role');
          if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA'
              && role !== 'combobox' && role !== 'switch' && e.target.tagName !== 'BUTTON') {
            e.preventDefault();
            onSubmit();
          } else if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        {/* Header — stable */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="h-9 w-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
            <FileText className="h-4.5 w-4.5 text-blue-600" style={{ height: 18, width: 18 }} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold tracking-tight text-slate-900 truncate" data-testid="client-code-modal-title">
              {editingCode ? `Edit Client Code${formData.code ? ` — ${formData.code}` : ''}` : 'Add Client Code'}
            </DialogTitle>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <DialogDescription className="text-xs text-slate-500 truncate">{editingCode ? (formData.client_name || 'Update the client code configuration') : 'Configure the new client code'}</DialogDescription>
              {editingCode && (
                <Badge className={`px-1.5 py-0 text-[10px] ${editingCode.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} variant="outline">
                  {editingCode.active ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Mobile section picker (below md) */}
        <div className="md:hidden px-4 py-2 border-b border-slate-200 shrink-0">
          <div className="relative">
            <select
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value)}
              className="w-full h-9 appearance-none rounded-md border border-slate-200 bg-white px-3 pr-8 text-sm text-slate-800"
              data-testid="client-code-section-select"
            >
              {SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}{errorSections.has(s.id) ? ' •' : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="h-4 w-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Body: nav + content */}
        <div className="flex flex-1 min-h-0">
          {/* Left section navigator (md+) */}
          <nav className="hidden md:flex w-52 shrink-0 flex-col gap-0.5 border-r border-slate-200 bg-slate-50/70 p-2.5 overflow-y-auto"
            data-testid="client-code-section-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = activeSection === s.id;
              return (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  data-testid={`cc-nav-${s.id}`}
                  className={`group flex items-center gap-2.5 rounded-md px-3 py-2 text-left transition-colors duration-150 ${
                    active ? 'bg-white border border-slate-200 shadow-sm' : 'border border-transparent hover:bg-white/70'
                  }`}>
                  <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm leading-tight ${active ? 'font-semibold text-slate-900' : 'font-medium text-slate-600'}`}>{s.label}</span>
                    <span className="block text-[11px] text-slate-400 leading-tight truncate">{s.desc}</span>
                  </span>
                  {errorSections.has(s.id) && (
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" data-testid={`cc-nav-error-${s.id}`} />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right configuration area */}
          <div className="flex-1 min-w-0 overflow-y-auto px-5 py-5 sm:px-7" data-testid="client-code-section-content">
            {renderSection()}
          </div>
        </div>

        {/* Sticky footer — stable */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/80 shrink-0">
          {Object.keys(formErrors).filter((k) => formErrors[k]).length > 0 && (
            <p className="hidden sm:block text-xs text-slate-400">Some sections need attention before saving.</p>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" onClick={onClose} data-testid="cancel-code-dialog">
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-code">
              {isLoading ? 'Saving…' : editingCode ? 'Update Client' : 'Create Client'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientCodeFormModal;
